import {
  Box,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { FaEdit, FaInbox, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { CrmTask } from "../../../models/Task";
import { crm } from "./crmTheme";

type SortDir = "asc" | "desc";
type SortField = "title" | "taskStatusName" | "responsibleName" | "dueDate" | "prospectName";

const cols: { label: string; field: SortField; render: (t: CrmTask) => string }[] = [
  { label: "Título", field: "title", render: (t) => t.title },
  { label: "Status", field: "taskStatusName", render: (t) => t.taskStatusName ?? "Sem status" },
  { label: "Responsável", field: "responsibleName", render: (t) => t.responsibleName ?? "—" },
  {
    label: "Vencimento",
    field: "dueDate",
    render: (t) => (t.dueDate ? new Date(t.dueDate).toLocaleDateString("pt-BR") : "—"),
  },
  { label: "Lead", field: "prospectName", render: (t) => t.prospectName ?? "—" },
];

const TaskTable = ({
  tasks,
  onEdit,
  onDelete,
}: {
  tasks: CrmTask[];
  onEdit: (task: CrmTask) => void;
  onDelete: (task: CrmTask) => void;
}) => {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const sorted = [...tasks].sort((a, b) => {
    if (!sortField) return 0;
    const va = String(a[sortField] ?? "");
    const vb = String(b[sortField] ?? "");
    return sortDir === "asc" ? va.localeCompare(vb, "pt-BR") : vb.localeCompare(va, "pt-BR");
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <FaSort color={crm.textFaint} />;
    return sortDir === "asc"
      ? <FaSortUp color={crm.brand} />
      : <FaSortDown color={crm.brand} />;
  };

  return (
    <Box
      bg={crm.surface}
      border={`1px solid ${crm.border}`}
      borderRadius="14px"
      boxShadow={crm.shadowSm}
      overflow="hidden"
    >
      <TableContainer overflowX="auto" overflowY="auto" maxH="calc(100vh - 280px)">
        <Table variant="simple" fontFamily="Inter" size="sm">
          <Thead>
            <Tr>
              {cols.map((col) => (
                <Th
                  key={col.field}
                  cursor="pointer"
                  onClick={() => handleSort(col.field)}
                  position="sticky"
                  top={0}
                  bg={crm.subtle}
                  zIndex={1}
                  textTransform="none"
                  color={crm.textMuted}
                  fontSize="xs"
                  fontWeight="700"
                  py="12px"
                  borderColor={crm.border}
                >
                  <Flex align="center" gap="6px">
                    {col.label}
                    <Box>
                      <SortIcon field={col.field} />
                    </Box>
                  </Flex>
                </Th>
              ))}
              <Th
                position="sticky"
                top={0}
                right={0}
                bg={crm.subtle}
                zIndex={2}
                textTransform="none"
                w="72px"
                borderColor={crm.border}
              />
            </Tr>
          </Thead>
          <Tbody>
            {sorted.length === 0 ? (
              <Tr>
                <Td colSpan={cols.length + 1}>
                  <Flex
                    flexDir="column"
                    align="center"
                    justify="center"
                    py="48px"
                    gap="10px"
                    color={crm.textFaint}
                  >
                    <FaInbox size={28} />
                    <Text fontSize="sm">Nenhuma task encontrada.</Text>
                    <Text fontSize="xs">
                      Clique em "Nova task" para adicionar.
                    </Text>
                  </Flex>
                </Td>
              </Tr>
            ) : (
              sorted.map((task) => (
                <Tr
                  key={task.id}
                  cursor="pointer"
                  _hover={{ bg: crm.brandSoft }}
                  onClick={() => onEdit(task)}
                  borderColor={crm.border}
                >
                  {cols.map((col) => (
                    <Td
                      key={col.field}
                      borderColor={crm.border}
                      fontSize="sm"
                      color={crm.text}
                      py="12px"
                    >
                      {col.render(task)}
                    </Td>
                  ))}
                  <Td
                    position="sticky"
                    right={0}
                    bg={crm.surface}
                    onClick={(e) => e.stopPropagation()}
                    borderColor={crm.border}
                  >
                    <Flex gap="4px">
                      <IconButton
                        aria-label="Editar task"
                        icon={<FaEdit />}
                        size="xs"
                        variant="ghost"
                        color={crm.textMuted}
                        _hover={{ color: crm.brand, bg: crm.brandSoft }}
                        onClick={() => onEdit(task)}
                      />
                      <IconButton
                        aria-label="Excluir task"
                        icon={<FaTrash />}
                        size="xs"
                        variant="ghost"
                        color={crm.textMuted}
                        _hover={{ color: crm.danger, bg: "#fef2f2" }}
                        onClick={() => onDelete(task)}
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TaskTable;
